import React, { FC, useContext, useEffect, useState } from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import Splitter from "m-react-splitters";
import "m-react-splitters/lib/splitters.css";

import IconAdd from "@material-ui/icons/Add";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import IconBrightness from "@material-ui/icons/Brightness1";

import RulesGrid from "./rulesgrid";
import { SnackContext } from "../../lib/context/SnackContext";
import { GenerateErrorMessage } from "../../lib/helpers/string_methods";
import {
  priceintegration,
  priceIntegrationColumns,
  priceintegrationMarket,
  PriceIntegrationMarket,
  PriceIntegrationRules,
  PriceIntegrationRulesType,
  RuleStatues,
  RuleTypes,
} from "../../lib/api/priceintegration";
import Confirmation from "../../lib/components/confirmation";
import Form, { FieldTypes } from "../../lib/components/AgileForm";
import MarketsGrid from "./marketsgrid";
import { GreenButton } from "../../lib/components/ColorButtons";

const StatusRobot = ({ color, tooltip }) => (
  <div>
    <Tooltip title={tooltip}>
      <IconBrightness style={{ color: color, display: "block", margin: "auto", border: "1px solid #333", borderRadius: "100%" }} />
    </Tooltip>
  </div>
);

const StatusFormatter = ({ status }: { status: string }) => {
  switch (status) {
    case "PERFECT":
      return <StatusRobot tooltip="PERFECT MATCH" color="green" />;
    case "BEST":
      return <StatusRobot tooltip="BEST MATCH" color="orange" />;
    case "NONE":
      return <StatusRobot tooltip="NO MATCH" color="red" />;
    default:
      return <StatusRobot tooltip="NO MATCH" color="red" />;
  }
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      height: "calc(100dvh - 100px)",
      width: "100%",
    },
    toolbarWrapper: {
      position: "relative",
    },
    toolbar: {
      position: "absolute",
      left: 0,
      width: "100%",
      gap: `${theme.spacing(1)}px`,
    },
    toolbarHeader: {
      display: "flex",
      flexDirection: "row",
      gap: "1rem",
      alignItems: "center",
      fontSize: "1.25rem",
      fontWeight: "bold",
      textTransform: "uppercase",
    },
  });

type Props = {} & WithStyles<typeof styles>;

export const PriceIntegrationRulesUnstyled: FC<Props> = ({ classes }) => {
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [data, setData] = useState<PriceIntegrationRules[]>([]);
  const [rule, setRule] = useState<PriceIntegrationRules | undefined>(undefined);
  const [removeRuleId, setRemoveRuleId] = useState<number>(0);

  const [markets, setMarkets] = useState<PriceIntegrationMarket[]>([]);
  const [market, setMarket] = useState<PriceIntegrationMarket | undefined>(undefined);
  const [removeMarketId, setRemoveMarketId] = useState<number>(0);

  const { updateSnack } = useContext(SnackContext);

  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await priceintegration.all();
      const marketResp = await priceintegrationMarket.all();

      const transformedResp = resp.map(item => ({
        ...item,
        sys_column: item.sys_column === "delivery_week" ? "cold_week" : item.sys_column
      }));

      setData(transformedResp);
      setMarkets(marketResp);
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error loading data");
      updateSnack({ message: err, color: "red", show: true });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadRule = async (rule_id: number) => {
    setFormLoading(true);
    try {
      const resp = await priceintegration.single(rule_id);
      setRule(resp);
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error loading Rule");
      updateSnack({ message: err, color: "red", show: true });
    }
    setFormLoading(false);
  };

  const handleSubmitRule = async (values: PriceIntegrationRules) => {
    setLoading(true);
    try {
      const id = values.id;
      delete values["id"];

      if (id == -99) {
        await priceintegration.create({ data: values });
      } else {
        await priceintegration.update(id, { data: values });
      }

      await loadData();
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error submitting Rule");
      updateSnack({ message: err, color: "red", show: true });
    }
    setLoading(false);
    setRule(undefined);
  };

  const handleRemoveRuleConfirm = async () => {
    setLoading(true);
    try {
      await priceintegration.remove(removeRuleId);
      await loadData();
      setRemoveRuleId(0);
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error removing Rule");
      updateSnack({ message: err, color: "red", show: true });
    }
    setLoading(false);
  };

  const loadMarket = async (market_id: number) => {
    setFormLoading(true);
    try {
      const resp = await priceintegrationMarket.single(market_id);
      setMarket(resp);
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error loading Market");
      updateSnack({ message: err, color: "red", show: true });
    }
    setFormLoading(false);
  };

  const handleSubmitMarket = async (values: PriceIntegrationMarket) => {
    setLoading(true);
    try {
      const id = values.id;
      delete values["id"];

      if (id == -99) {
        await priceintegrationMarket.create({ data: values });
      } else {
        await priceintegrationMarket.update(id, { data: values });
      }
      await loadData();
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error submitting Market");
      updateSnack({ message: err, color: "red", show: true });
    }
    setLoading(false);
    setMarket(undefined);
  };

  const handleRemoveMarketConfirm = async () => {
    setLoading(true);
    try {
      await priceintegrationMarket.remove(removeMarketId);
      await loadData();
      setRemoveMarketId(0);
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error removing Market");
      updateSnack({ message: err, color: "red", show: true });
    }
    setLoading(false);
  };

  return (
    <div className={classes.root}>
      <Confirmation
        isOpen={removeRuleId}
        handleClose={() => setRemoveRuleId(0)}
        handleConfirm={() => handleRemoveRuleConfirm()}
        title="Remove Price Integration Rule"
        body="Are you sure you want to remove this Rule?"
      />
      <Confirmation
        isOpen={removeMarketId}
        handleClose={() => setRemoveMarketId(0)}
        handleConfirm={() => handleRemoveMarketConfirm()}
        title="Remove Price Integration Market"
        body="Are you sure you want to remove this Market?"
      />
      <Confirmation isOpen={!!rule} handleClose={() => setRule(null)} handleConfirm={() => {}} title={`Edit Price Integration Rule`} body={null}>
        <Form
          initValues={rule}
          onClose={() => setRule(undefined)}
          onSubmit={(values) => handleSubmitRule(values)}
          fields={[
            { field: "pii_column", title: "PI Column", type: FieldTypes.Select, data: priceIntegrationColumns },
            { field: "sys_column", title: "Shipment Column", type: FieldTypes.Select, data: priceIntegrationColumns },
            { field: "match", title: "Type", type: FieldTypes.Select, data: RuleTypes },
            { field: "status", title: "Status", type: FieldTypes.Select, data: RuleStatues },
          ]}
        />
      </Confirmation>
      <Confirmation
        isOpen={!!market}
        handleClose={() => setMarket(null)}
        handleConfirm={() => {}}
        title={`${market && market.id != -99 ? "Edit" : "Add"} Price Integration Market`}
        body={null}
      >
        <Form
          initValues={market}
          onClose={() => setMarket(undefined)}
          onSubmit={(values) => handleSubmitMarket(values)}
          fields={[
            { field: "source_market", title: "Source", type: FieldTypes.Text },
            { field: "target_market", title: "Target", type: FieldTypes.Text },
          ]}
        />
      </Confirmation>
      <Splitter postPoned dispatchResize position="horizontal" primaryPaneMaxHeight="50%" primaryPaneHeight="50%" primaryPaneWidth="100%" onDragFinished={(e) => {}}>
        <Splitter postPoned dispatchResize position="vertical" primaryPaneMaxHeight="100%" primaryPaneHeight="100%" primaryPaneWidth="50%" onDragFinished={(e) => {}}>
          <div className={classes.toolbarWrapper}>
            <Toolbar className={classes.toolbar}>
              <GreenButton
                title="Add New Rule"
                variant="contained"
                onClick={() => setRule({ id: -99, pii_column: "", sys_column: "", match: 1, status: 1 })}
                style={{ marginRight: "1em", backgroundColor: "green", color: "white" }}
              >
                <IconAdd />
              </GreenButton>
              <p className={classes.toolbarHeader}>
                Perfect Match <StatusFormatter status="PERFECT" />
              </p>
            </Toolbar>
            <RulesGrid
              data={data.filter((row) => row.match === PriceIntegrationRulesType.PERFECT)}
              loading={loading}
              handleEdit={loadRule}
              handleRemove={(id: number) => setRemoveRuleId(id)}
            />
          </div>
          <div className={classes.toolbarWrapper}>
            <Toolbar className={classes.toolbar}>
              <GreenButton
                title="Add New Rule"
                variant="contained"
                onClick={() => setRule({ id: -99, pii_column: "", sys_column: "", match: 2, status: 1 })}
                style={{ marginRight: "1em", backgroundColor: "green", color: "white" }}
              >
                <IconAdd />
              </GreenButton>
              <p className={classes.toolbarHeader}>
                Best Match <StatusFormatter status="BEST" />
              </p>
            </Toolbar>
            <RulesGrid
              data={data.filter((row) => row.match === PriceIntegrationRulesType.BEST)}
              loading={loading}
              handleEdit={loadRule}
              handleRemove={(id: number) => setRemoveRuleId(id)}
            />
          </div>
        </Splitter>
        <Splitter postPoned dispatchResize position="vertical" primaryPaneMaxHeight="50%" primaryPaneHeight="50%" primaryPaneWidth="50%" onDragFinished={(e) => {}}>
          <div className={classes.toolbarWrapper}>
            <Toolbar className={classes.toolbar}>
              <GreenButton
                title="Add New Rule"
                variant="contained"
                onClick={() => setRule({ id: -99, pii_column: "", sys_column: "", match: 3, status: 1 })}
                style={{ marginRight: "1em", backgroundColor: "green", color: "white" }}
              >
                <IconAdd />
              </GreenButton>
              <p className={classes.toolbarHeader}>
                No Match <StatusFormatter status="NONE" />
              </p>
            </Toolbar>
            <RulesGrid
              data={data.filter((row) => row.match === PriceIntegrationRulesType.NOTHING)}
              loading={loading}
              handleEdit={loadRule}
              handleRemove={(id: number) => setRemoveRuleId(id)}
            />
          </div>
          <div className={classes.toolbarWrapper}>
            <Toolbar className={classes.toolbar}>
              <GreenButton
                title="Add New Market"
                variant="contained"
                onClick={() => setMarket({ id: -99, target_market: "", source_market: "" })}
                style={{ marginRight: "1em", backgroundColor: "green", color: "white" }}
              >
                <IconAdd />
              </GreenButton>
              <p className={classes.toolbarHeader}>Market Rules</p>
            </Toolbar>
            <MarketsGrid data={markets} loading={loading} handleEdit={loadMarket} handleRemove={(id: number) => setRemoveMarketId(id)} />
          </div>
        </Splitter>
      </Splitter>
    </div>
  );
};

export default withStyles(styles)(PriceIntegrationRulesUnstyled);
